import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { animateScroll as scroll } from 'react-scroll/modules';
import { v4 as uuid } from 'uuid';

import { postMessageRN, runningInRNWebView, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { InfoMessage } from '@eika/attention-message';
import { Button, ButtonTypes } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';
import { RichIconTypes } from '@eika/rich-icon';

import { AppState } from '../../reducers';

import './Message.scss';

type ErrorCardProps = import('@eika/card/src/ErrorCard').ErrorCardProps;

interface Props extends ErrorCardProps {
    title: string;
    textList?: string[];
    link?: string;
    linkText?: string;
    infoBox?: string;
    showExtendedException?: boolean;
}

const ErrorMessage: React.FC<Props> = ({
    icon = RichIconTypes.HJERTE_M_PLASTER,
    title,
    textList,
    link,
    linkText,
    infoBox,
    children,
    exception,
    isModal = true,
    showExtendedException = false,
    ...props
}) => {
    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    const text = useSelector((state: AppState) => state.context.config.data.text);
    const errorException = useSelector((state: AppState) => state.error.exception);
    const errorMessageException = exception || errorException;

    const copyToClipboard = useCallback((data: string) => {
        if (runningInRNWebView) {
            postMessageRN({
                action: WebToRNAction.COPY_TEXT,
                payload: data,
            });
        } else {
            navigator.clipboard.writeText(data);
        }
    }, []);

    return (
        <div className="message">
            <div className="sgw-layout medium-centered-container">
                <div className="sgw-layout-item sgw-layout-item__full-width">
                    <ErrorCard
                        className="message__error-card"
                        icon={icon}
                        title={title}
                        isModal={isModal}
                        exception={showExtendedException ? errorMessageException : undefined}
                        {...props}
                    >
                        <div className="message__content">
                            {textList && textList.map((c) => !!c && <p key={uuid()}>{c}</p>)}
                            {children}
                        </div>

                        {link && (
                            <div className="message__cta">
                                <Link href={link} linkType={LinkType.BUTTON_STANDARD_POSITIVE}>
                                    {linkText}
                                </Link>
                            </div>
                        )}

                        {errorMessageException?.rcid && (
                            <p>
                                <Button
                                    buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                    onClick={() => copyToClipboard(errorMessageException.rcid!)}
                                    aria-label={text.errorMessageAriaLabel}
                                    icon={IconTypes.COPY_24}
                                >
                                    {text.errorMessageErrorCode} <span>{errorMessageException.rcid}</span>
                                </Button>
                            </p>
                        )}
                    </ErrorCard>

                    {infoBox && <InfoMessage className="message__info" text={infoBox} icon={IconTypes.LYSPAERE_24} />}
                </div>
            </div>
        </div>
    );
};
export default ErrorMessage;
